<script setup lang="ts">
    // @ts-nocheck
    import { onBeforeMount, onBeforeUnmount, ref, toRefs, computed } from "vue";
    import { getConfig, getErrorMessage, klinikenApi } from "@/api";
    import { openDialog } from "@/utils";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KRepeater from "@/components/forms/groups/Repeater/KRepeater.vue";
    import KNumber from "@/components/forms/inputs/Number/KNumber.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";
    import { Vardplatsbestallning, VardplatsStatus } from "@/tabs/Vardplatsbokning/types.ts";
    import { useStore } from "@/store";
    import KCheckbox from "@/components/forms/inputs/Checkbox/KCheckbox.vue";

    const store = useStore();

    const props = defineProps<{
        vardplatsbestallningId?: string;
    }>();

    const { vardplatsbestallningId } = toRefs(props);

    const formRef = ref();
    const vardpersoner = ref([]);
    const vardformer = ref([]);
    const vardgivare = ref([]);
    const isBookedAndExternal = ref(false);

    const hasUnsavedData = () => {
        return formRef?.value?.hasUnsavedData();
    };

    defineExpose({
        hasUnsavedData,
    });

    const getFormTriggers = () => {
        return [
            {
                fieldName: "operator",
                dependentOn: "vardgivare",
                action: loadAndMapVardpersonerFromVardgivare,
            },
            {
                fieldName: "installelsedatum",
                dependentOn: "operationsdatum",
                action: emptyInstallelsedatum,
            },
            {
                fieldName: "anhorig_namn",
                dependentOn: "anhorigplats",
                action: ({ value, dependentOnValue }: { value: unknown; dependentOnValue: unknown }) => {
                    if (dependentOnValue) {
                        return {
                            disabled: false,
                            required: true,
                        };
                    } else {
                        return {
                            disabled: true,
                            required: false,
                            value: null,
                        };
                    }
                },
            },
        ];
    };

    const converters = [
        {
            fieldName: "operationstid",
            converter: ({ value }) => {
                if (!value) {
                    value = null;
                }

                return value;
            },
        },
        {
            fieldName: "installelsetid",
            converter: ({ value }) => {
                if (!value) {
                    value = null;
                }

                return value;
            },
        },
    ];

    const loadAndMapVardgivare = async () => {
        try {
            const response = await klinikenApi.get("/core/externavardgivare/", getConfig());
            vardgivare.value = response.data.results.map((item: any) => {
                return {
                    value: item.id,
                    label: item.alias,
                };
            });
        } catch (e) {
            openDialog(getErrorMessage(e), "error");
        }
    };

    const loadAndMapVardpersonerFromVardgivare = async ({
        value,
        dependentOnValue,
    }: {
        value: unknown;
        dependentOnValue: unknown;
    }) => {
        if (dependentOnValue) {
            try {
                const response = await klinikenApi.get(
                    "/core/externavardpersoner/",
                    getConfig({
                        params: {
                            vardgivare: dependentOnValue,
                        },
                    })
                );
                vardpersoner.value = response.data.results.map((item: any) => {
                    return {
                        value: item.id,
                        label: `${item.fornamn} ${item.efternamn}`,
                    };
                });
            } catch (e) {
                openDialog(getErrorMessage(e), "error");
            }
        } else {
            vardpersoner.value = [];
        }

        if (vardpersoner.value.find((vardperson) => vardperson.value === value)) {
            return { value };
        } else {
            formRef?.value?.setFieldValue({ name: "operator", value: null });
            return { value: null };
        }
    };

    const loadVardformer = async () => {
        try {
            const response = await klinikenApi.get(
                "/operation/kodverk/vardformer/",
                getConfig({ params: { typ: "SLUTENVÅRD" } })
            );
            vardformer.value = response.data.results.map((item: any) => {
                return {
                    value: item.id,
                    label: item.namn,
                };
            });
        } catch (e) {
            openDialog(getErrorMessage(e), "error");
        }
    };

    const emptyInstallelsedatum = async ({
        value,
        dependentOnValue,
    }: {
        value: unknown;
        dependentOnValue: unknown;
    }) => {
        // Om operationsdatum ändras av någon som inte har skrivrätt på slutenvårdsbesök ska inställelsedatum tömmas så
        // att det tvingas att ta ställning till nytt inställelsedatum
        if (!store.getters["ability/can"]({ action: "update", subject: "slutenvardsbesok" })) {
            const dependentOnInitialValue = formRef?.value?.getFieldInitialValue({ name: "operationsdatum" });
            if (dependentOnInitialValue && dependentOnValue !== dependentOnInitialValue) {
                // FIXME return { value: null }; fungerar inte av nån anledning så det sätts hår också
                formRef?.value?.setFieldValue({ name: "installelsedatum", value: null });
                return { value: null };
            }
        }
        return { value: value };
    };

    const fetchFormData = async () => {
        const vardplatsbestallning: Vardplatsbestallning | undefined = await store.dispatch(
            "vardplatsbestallning/getVardplatsbestallningById",
            { id: vardplatsbestallningId?.value }
        );

        if (vardplatsbestallning) {
            // Är statusen på slutenvårdsbesöket bokad ska bara de med skrivrätt på slutenvårdsbesök kunna uppdatera
            // vissa fält
            isBookedAndExternal.value =
                vardplatsbestallning.status === VardplatsStatus.BOKAD &&
                !store.getters["ability/can"]({ action: "update", subject: "slutenvardsbesok" });

            return {
                patient: vardplatsbestallning.patient.id,
                externt_id: vardplatsbestallning.externt_id,
                operationsdatum: vardplatsbestallning.operationsdatum,
                operationstid: vardplatsbestallning.operationstid,
                installelsedatum: vardplatsbestallning.installelsedatum,
                installelsetid: vardplatsbestallning.installelsetid,
                antal_varddygn: vardplatsbestallning.antal_varddygn,
                vardgivare: vardplatsbestallning.vardgivare.id,
                operator: vardplatsbestallning.operator.id,
                sida: vardplatsbestallning.sida,
                typoperation: vardplatsbestallning.typoperation,
                huvuddiagnos: vardplatsbestallning.huvuddiagnos.id,
                huvudoperationskod: vardplatsbestallning.huvudoperationskod.id,
                bioperationskoder: vardplatsbestallning.bioperationskoder.map((opkod: { id: number }) => {
                    return { id: opkod.id };
                }),
                info_till_vardavdelningen: vardplatsbestallning.info_till_vardavdelningen,
                anhorigplats: vardplatsbestallning.anhorigplats,
                anhorig_namn: vardplatsbestallning.anhorig_namn,
                vardform: vardplatsbestallning.vardform ? vardplatsbestallning.vardform.id.toString() : undefined,
            };
        }

        return null;
    };

    const emit = defineEmits(["submit", "cancel"]);

    const submit = (data: unknown) => {
        emit("submit", data);
    };

    const cancel = (data: { target?: string | undefined; hasUnsavedData: boolean }) => {
        emit("cancel", data);
    };

    onBeforeMount(async () => {
        store.commit("vardplatsbestallning/addSubscriber");

        await loadVardformer();
        await loadAndMapVardgivare();
    });

    onBeforeUnmount(() => {
        store.commit("vardplatsbestallning/removeSubscriber");
    });
</script>

<template>
    <KForm
        ref="formRef"
        id="vardplatsbestallning-form"
        class="vardplatsbestallning-form"
        @submit="submit"
        @cancel="cancel"
        submit-content="Spara vårdplatsbeställning"
        cancel-content="Avbryt"
        :initial-values-getter="fetchFormData"
        :triggers="getFormTriggers()"
        :converters="converters"
    >
        <template #default>
            <div class="form-content">
                <FormulateInput
                    type="datepicker"
                    name="operationsdatum"
                    label="Operationsdatum *"
                    validation="required"
                    validation-name="Operationsdatum"
                    :disabled="isBookedAndExternal"
                />

                <FormulateInput type="timepicker" name="operationstid" label="Operationstid" />

                <FormulateInput
                    type="datepicker"
                    name="installelsedatum"
                    label="Inställelsedatum *"
                    validation="required"
                    validation-name="Inställelsedatum"
                    :disabled="isBookedAndExternal"
                />

                <FormulateInput
                    type="timepicker"
                    name="installelsetid"
                    label="Inställelsetid"
                    validation-name="Inställelsetid"
                />

                <KNumber name="antal_varddygn" label="Antal vårddygn" validation="required|min:1" />

                <FormulateInput
                    type="vueselect"
                    name="vardgivare"
                    label="Vårdgivare *"
                    validation="required"
                    validation-name="Vårdgivare"
                    :options="vardgivare"
                />

                <FormulateInput
                    type="vueselect"
                    name="operator"
                    label="Operatör *"
                    validation="required"
                    validation-name="Operatör"
                    :options="vardpersoner"
                />

                <FormulateInput
                    type="vueselect"
                    name="vardform"
                    label="Vårdform *"
                    validation="required"
                    validation-name="Vårdform"
                    :options="vardformer"
                />

                <FormulateInput
                    type="select"
                    name="sida"
                    label="Sida"
                    :options="[
                        { value: '', label: '' },
                        { value: 'HÖGER_SIDA', label: 'Höger sida' },
                        { value: 'VÄNSTER_SIDA', label: 'Vänster sida' },
                        { value: 'BILATERALT', label: 'Bilateralt' },
                        { value: 'EJ_AKTUELLT', label: 'Ej aktuellt' },
                    ]"
                />

                <KText name="typoperation" label="Typoperation" class="span-three" />

                <FormulateInput
                    type="autocomplete"
                    name="huvuddiagnos"
                    label="Huvuddiagnos *"
                    validation="required"
                    validation-name="Huvuddiagnos"
                    url="/core/kodverk/icdkoder/"
                    class="span-three"
                />

                <FormulateInput
                    type="autocomplete"
                    name="huvudoperationskod"
                    label="Huvudoperationskod *"
                    validation="required"
                    validation-name="Huvudoperationskod"
                    url="/operation/kodverk/operationskoder/"
                    class="span-three"
                />

                <KRepeater
                    name="bioperationskoder"
                    add-label="Lägg till bioperationskod"
                    remove-label="Ta bort bioperationskod"
                >
                    <FormulateInput
                        name="id"
                        type="autocomplete"
                        url="/operation/kodverk/operationskoder/"
                        label="Bioperationskod"
                        validation-name="Bioperationskod"
                        class="span-three"
                    />
                </KRepeater>

                <KCheckbox name="anhorigplats" label="Anhörigplats" valueType="boolean" />

                <KText name="anhorig_namn" label="Anhörig namn" type="text" :disabled="true" />

                <KText
                    name="info_till_vardavdelningen"
                    label="Info till vårdavdelningen"
                    type="textarea"
                    class="span-two"
                />

                <FormulateErrors />
            </div>
        </template>
    </KForm>
</template>
